/**
 * Invoicing admin API — `/api/v2/invoicing/*`. All endpoints are
 * admin-only (Bearer + role admin, handled by the shared instance).
 * See ./invoicingProducts.ts and ./invoicingBillingAddresses.ts for the
 * catalog + billing-address sub-resources.
 */
import instance, { ADMIN_PREFIX } from 'services/api';
import {
  CountryOption,
  CreateInvoiceInput,
  CreateInvoiceResponse,
  CreditNoteInput,
  Invoice,
  InvoiceDetail,
  InvoiceListFilters,
  InvoiceListResponse,
  MarkPaidInput,
  NumberCounter,
  PartyResult
} from 'types/api/invoicing';

const BASE = `${ADMIN_PREFIX}/invoicing`;

/**
 * `GET /invoicing/countries?lang=` — ISO-3166 alpha-2 list, localized + sorted
 * by name. The authoritative source for the country selects (matches the BE's
 * country validation).
 */
export const fetchCountries = async (
  lang: 'en' | 'de' = 'en'
): Promise<CountryOption[]> => {
  const res = await instance.get<CountryOption[]>(`${BASE}/countries`, {
    params: { lang }
  });

  return res.data ?? [];
};

/**
 * `GET /invoicing/:ref/creditable` — how much of this invoice can still be
 * credited, in major units. Several partial credit notes are allowed, so this
 * shrinks with each one; use it to bound the amount input.
 */
export const fetchCreditable = async (publicId: string): Promise<number> => {
  const res = await instance.get<{ remaining: number }>(
    `${BASE}/${publicId}/creditable`
  );

  return Number(res.data?.remaining ?? 0);
};

/**
 * `POST /invoicing/:ref/credit-notes` — issue a credit note against this
 * invoice. Returns the note: a document with a NEGATIVE total, numbered from
 * the same sequence as invoices.
 *
 * Refused by the BE on a draft, on a cancelled invoice, on a credit note
 * itself, over the remaining amount, and for a Stripe refund where there is
 * no Stripe payment — each with its own message, so surface the error.
 */
export const createCreditNote = async (
  publicId: string,
  input: CreditNoteInput
): Promise<Invoice> => {
  const res = await instance.post<Invoice>(
    `${BASE}/${publicId}/credit-notes`,
    input
  );

  return res.data;
};

/**
 * `GET /invoicing/parties?search=&limit=` — one search across BOTH party
 * tables (platform patients and billing-only customers).
 *
 * Replaces `GET /patients?search=` for the invoice party picker only; that
 * endpoint is still correct everywhere else, it simply cannot see billing
 * customers. An empty search returns [] — this is a picker, not a listing.
 *
 * `search` also accepts a birthday (DD.MM.YYYY or YYYY-MM-DD), on its own or
 * combined with a name. Deleted patients are excluded by the BE.
 */
export const fetchParties = async (
  search: string,
  limit = 20
): Promise<PartyResult[]> => {
  if (!search.trim()) {
    return [];
  }

  const res = await instance.get<PartyResult[]>(`${BASE}/parties`, {
    params: { search, limit },
    // An admin invoices any party regardless of the org picked in the header.
    headers: { organization: null as unknown as string }
  });

  return res.data ?? [];
};

/** `GET /invoicing` — filtered, paginated list. Envelope: {data,total,page,limit}. */
export const fetchInvoices = async (
  filters: InvoiceListFilters = {}
): Promise<InvoiceListResponse> => {
  const res = await instance.get<InvoiceListResponse>(BASE, {
    params: filters
  });

  return {
    data: res.data.data ?? [],
    total: res.data.total ?? 0,
    page: res.data.page ?? 1,
    limit: res.data.limit ?? 0
  };
};

/** `GET /invoicing/:publicId` — single invoice with its lines. */
export const fetchInvoice = async (
  publicId: string
): Promise<InvoiceDetail> => {
  const res = await instance.get<InvoiceDetail>(`${BASE}/${publicId}`);

  return res.data;
};

/** `POST /invoicing` — create (and, when issue!==false, issue) an invoice. */
export const createInvoice = async (
  input: CreateInvoiceInput
): Promise<CreateInvoiceResponse> => {
  const res = await instance.post<CreateInvoiceResponse>(BASE, input);

  return res.data;
};

/**
 * `POST /invoicing/preview` — live PDF preview (nothing persisted; `issue`
 * ignored). Returns the rendered PDF as a Blob for an <iframe> via
 * URL.createObjectURL. Debounce the call on the FE.
 */
export const previewInvoicePdf = async (
  input: CreateInvoiceInput
): Promise<Blob> => {
  const res = await instance.post(`${BASE}/preview`, input, {
    responseType: 'blob'
  });

  return res.data as Blob;
};

/**
 * `POST /invoicing/preview/data` — same body as create; returns JSON with
 * computed totals + rendered fields (for drawing the preview as HTML
 * instead of the PDF). Shape is BE-defined; kept loose until needed.
 */
export const previewInvoiceData = async (
  input: CreateInvoiceInput
): Promise<unknown> => {
  const res = await instance.post(`${BASE}/preview/data`, input);

  return res.data;
};

/**
 * `GET /invoicing/:publicId/pdf` — 302-redirects to a short-lived signed
 * S3 URL; axios follows the redirect and returns the PDF Blob.
 */
export const fetchInvoicePdf = async (publicId: string): Promise<Blob> => {
  const res = await instance.get(`${BASE}/${publicId}/pdf`, {
    responseType: 'blob'
  });

  return res.data as Blob;
};

/** `GET /invoicing/export.csv` — accounting export (same filters). */
export const exportInvoicesCsv = async (
  filters: InvoiceListFilters = {}
): Promise<Blob> => {
  const res = await instance.get(`${BASE}/export.csv`, {
    params: filters,
    responseType: 'blob'
  });

  return res.data as Blob;
};

/** `POST /invoicing/:publicId/cancel` — void (keeps its number); 400 if paid. */
export const cancelInvoice = (publicId: string) =>
  instance.post<Invoice>(`${BASE}/${publicId}/cancel`);

/** `POST /invoicing/:publicId/mark-paid` — bank-transfer paid flow. */
export const markInvoicePaid = (publicId: string, input: MarkPaidInput = {}) =>
  instance.post<Invoice>(`${BASE}/${publicId}/mark-paid`, input);

/**
 * `POST /invoicing/:ref/send` — on-demand email the invoice to the patient.
 * Paid → paid-invoice email; issued → PDF + pay link; draft → 400.
 * `sent:false` means no email on file or the mailer failed.
 */
export const sendInvoice = async (
  ref: string
): Promise<{ sent: boolean; email: string | null }> => {
  const res = await instance.post<{ sent: boolean; email: string | null }>(
    `${BASE}/${ref}/send`
  );

  return res.data;
};

// --- Invoice-number controls (super_admin only; regular admin gets 403) ---

/** `GET /invoicing/number-counter[?prefix=]` — last/next issued number. */
export const fetchNumberCounter = async (
  prefix?: string
): Promise<NumberCounter> => {
  const res = await instance.get<NumberCounter>(`${BASE}/number-counter`, {
    params: prefix ? { prefix } : undefined
  });

  return res.data;
};

/**
 * `PUT /invoicing/number-counter` — set the NEXT invoice number (forward-only;
 * 400 below current next, 409 if ≤ the highest already used).
 */
export const setNumberCounter = async (
  nextNumber: number,
  prefix?: string
): Promise<NumberCounter> => {
  const res = await instance.put<NumberCounter>(`${BASE}/number-counter`, {
    nextNumber,
    ...(prefix ? { prefix } : {})
  });

  return res.data;
};

/** `PATCH /invoicing/:ref/number` — correct one invoice's number (409 dup). */
export const updateInvoiceNumber = (ref: string, invoiceNumber: string) =>
  instance.patch<Invoice>(`${BASE}/${ref}/number`, { invoiceNumber });
